import { useState } from 'react';
import { Sparkles, Search, Loader, Star, TrendingUp, Award, BookOpen } from 'lucide-react';
import { getPersonalizedRecommendations } from '../../services/recommendations';

export default function RecommendationsPanel() {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [filters, setFilters] = useState({
    level: '',
    sector: '',
    limit: 10
  });
  const [error, setError] = useState(null);

  const handleGetRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPersonalizedRecommendations(
        null, // userId (null = utilisateur connecté)
        null, // competences (null = utiliser le profil)
        filters.level || null,
        filters.sector || null,
        filters.limit
      );
      setRecommendations(result.recommendations || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la récupération des recommandations');
      console.error('Erreur recommandations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score > 0.8) return 'text-green-600 bg-green-100';
    if (score > 0.6) return 'text-blue-600 bg-blue-100';
    if (score > 0.4) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Recommandations Personnalisées</h2>
          <p className="text-sm text-gray-600">Formations adaptées à votre profil</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
          <select
            value={filters.level}
            onChange={(e) => setFilters({ ...filters, level: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">Tous les niveaux</option>
            <option value="niveau_3">Niveau 3 (CAP)</option>
            <option value="niveau_4">Niveau 4 (BAC)</option>
            <option value="niveau_5">Niveau 5 (BAC+2)</option>
            <option value="niveau_6">Niveau 6 (BAC+3/4)</option>
            <option value="niveau_7">Niveau 7 (BAC+5)</option>
            <option value="niveau_8">Niveau 8 (Doctorat)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Secteur</label>
          <input
            type="text"
            value={filters.sector}
            onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
            placeholder="Ex: Informatique"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
          <input
            type="number"
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
            min="5"
            max="50"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Bouton */}
      <button
        onClick={handleGetRecommendations}
        disabled={loading}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Génération en cours...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Obtenir des recommandations
          </>
        )}
      </button>

      {/* Erreur */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Résultats */}
      {recommendations.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {recommendations.length} recommandation{recommendations.length > 1 ? 's' : ''} trouvée{recommendations.length > 1 ? 's' : ''}
            </h3>
            <div className="text-sm text-gray-500">
              Score moyen : {Math.round((recommendations.reduce((sum, r) => sum + r.score, 0) / recommendations.length) * 100)}%
            </div>
          </div>

          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {recommendations.map((rec, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-4 h-4 text-purple-600" />
                      <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {rec.niveau && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {rec.niveau}
                        </span>
                      )}
                      {rec.secteur && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {rec.secteur}
                        </span>
                      )}
                      {rec.type && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {rec.type}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mb-3">{rec.explanation}</p>

                    {rec.matchingReasons && rec.matchingReasons.competences && rec.matchingReasons.competences.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {rec.matchingReasons.competences.slice(0, 5).map((comp, i) => (
                          <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                            {comp}
                          </span>
                        ))}
                        {rec.matchingReasons.competences.length > 5 && (
                          <span className="px-2 py-0.5 text-gray-500 text-xs">
                            +{rec.matchingReasons.competences.length - 5}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-center gap-1">
                    <div className={`px-3 py-2 rounded-lg font-bold text-lg ${getScoreColor(rec.score)}`}>
                      {Math.round(rec.score * 100)}%
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3 h-3 ${
                            i < Math.round(rec.score * 5)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && recommendations.length === 0 && !error && (
        <div className="mt-6 text-center py-12 text-gray-500">
          <Award className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Cliquez sur le bouton pour obtenir vos recommandations personnalisées</p>
        </div>
      )}
    </div>
  );
}
